/** Hash editors can put on a CMS or shared product URL to open the files popup. */
export const PRODUCT_FILES_HASH = '#product-files';

/** Query-param alternative, matching enquiry's `?enquire=1`. */
export const PRODUCT_FILES_QUERY_KEY = 'files';
export const PRODUCT_FILES_QUERY_VALUE = '1';

type LocationLike = Pick<Location, 'pathname' | 'search' | 'hash' | 'href'>;

/** True for in-page anchors such as `#product-files`. */
export const isHashOnlyHref = (href: string) => href.trim().startsWith('#');

/**
 * Whether the current URL should open the product files popup on load.
 */
export const shouldOpenProductFilesFromLocation = (location: LocationLike) => {
    if (location.hash === PRODUCT_FILES_HASH) {
        return true;
    }

    return (
        new URLSearchParams(location.search).get(PRODUCT_FILES_QUERY_KEY) ===
        PRODUCT_FILES_QUERY_VALUE
    );
};

/**
 * Whether a clicked href targets the files popup on the current product page.
 */
export const isSamePageProductFilesHref = (
    href: string,
    location: LocationLike,
) => {
    const trimmed = href.trim();
    if (!trimmed) return false;
    if (trimmed === PRODUCT_FILES_HASH) return true;

    try {
        const url = new URL(trimmed, location.href);
        if (url.pathname !== location.pathname) return false;

        return (
            url.hash === PRODUCT_FILES_HASH ||
            url.searchParams.get(PRODUCT_FILES_QUERY_KEY) ===
                PRODUCT_FILES_QUERY_VALUE
        );
    } catch {
        return false;
    }
};

/**
 * Drops `#product-files` / `?files=1` after the popup closes so the same
 * in-page link can open it again.
 */
export const stripProductFilesDeepLink = () => {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);
    let changed = false;

    if (url.hash === PRODUCT_FILES_HASH) {
        url.hash = '';
        changed = true;
    }

    if (
        url.searchParams.get(PRODUCT_FILES_QUERY_KEY) ===
        PRODUCT_FILES_QUERY_VALUE
    ) {
        url.searchParams.delete(PRODUCT_FILES_QUERY_KEY);
        changed = true;
    }

    if (!changed) return;

    window.history.replaceState(
        null,
        '',
        `${url.pathname}${url.search}${url.hash}`,
    );
};
